import { useEffect, useState } from "react";
import { Radar, X, ChevronDown, ChevronUp, CheckCircle, XCircle, Mail, FileText, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { getDiscoveryRun } from "../lib/api";

const STAGES = {
  starting: "Starting…",
  searching: "Searching Google…",
  checking: "Checking candidate sites…",
  done: "Done",
};

const CHECK_LABELS = {
  on_topic: "On-topic only",
  min_traffic: "Min traffic",
  active_within_days: "Active blogs only",
  hide_link_farms: "Hide link farms",
  hide_paid: "Hide paid posts",
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-xl font-bold">{value ?? "–"}</p>
  </div>
);

export default function DiscoveryRunPanel({ runId, onUpdate, onClose }) {
  const [run, setRun] = useState(null);
  const [showCandidates, setShowCandidates] = useState(false);

  useEffect(() => {
    let timer;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await getDiscoveryRun(runId);
        if (stopped) return;
        setRun(data);
        onUpdate?.(data);
        if (data.status === "running") timer = setTimeout(poll, 3000);
      } catch (error) {
        if (!stopped) timer = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  if (!run) return null;

  const running = run.status === "running";
  const activeChecks = Object.entries(run.checks || {})
    .filter(([, v]) => v)
    .map(([k, v]) => (typeof v === "number" ? `${CHECK_LABELS[k]} (${v.toLocaleString()})` : CHECK_LABELS[k]));
  const candidates = [...(run.candidates || [])].sort((a, b) => (a.status === b.status ? 0 : a.status === "accepted" ? -1 : 1));

  return (
    <Card className="bg-card border-border" data-testid="discovery-run-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {running ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <Radar className="w-5 h-5 text-primary" />}
              Discovery: {run.industry}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {run.status === "failed" ? `Failed: ${run.error}` : STAGES[run.stage] || run.stage}
              {" · "}
              {activeChecks.length ? `Checks: ${activeChecks.join(", ")}` : "No optional checks"}
              {run.search_source === "cache" && " · search results from cache"}
              {run.search_cost_usd > 0 && ` · search cost $${run.search_cost_usd}`}
            </p>
          </div>
          {!running && (
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          <Stat label="Search results" value={run.raw_results} />
          <Stat label="Candidate sites" value={run.candidates_total} />
          <Stat label="Checked" value={run.checked} />
          <Stat label="Verified sites" value={`${run.accepted} / ${run.max_results}`} />
          <Stat label="With email" value={run.with_email} />
          <Stat label="Form only" value={run.with_form_only ?? (running ? "…" : 0)} />
        </div>

        {run.skipped && (
          <p className="text-xs text-muted-foreground">
            Skipped before checking: {run.skipped.already_in_database} already saved, {run.skipped.excluded} excluded domains,{" "}
            {run.skipped.not_a_guest_post_url} article/other pages{run.list_pages_used ? ` · ${run.list_pages_used} "list of sites" articles mined for links` : ""}
          </p>
        )}

        {run.rejection_summary && Object.keys(run.rejection_summary).length > 0 && (
          <div className="text-sm">
            <p className="font-medium mb-1">Why sites were rejected</p>
            <ul className="text-muted-foreground space-y-0.5">
              {Object.entries(run.rejection_summary)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, count]) => (
                  <li key={reason}>{count} × {reason}</li>
                ))}
            </ul>
          </div>
        )}

        {candidates.length > 0 && (
          <div>
            <Button variant="outline" size="sm" onClick={() => setShowCandidates((v) => !v)}>
              {showCandidates ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              {showCandidates ? "Hide" : "Show"} all {candidates.length} checked sites
            </Button>
            {showCandidates && (
              <div className="mt-3 max-h-96 overflow-y-auto border border-border rounded-md">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Result</th>
                      <th>Reason</th>
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.domain}>
                        <td>
                          <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">
                            {c.domain}
                          </a>
                          {c.source === "list page" && <span className="ml-1 text-xs text-muted-foreground">(from list)</span>}
                        </td>
                        <td>
                          {c.status === "accepted" ? (
                            <span className="inline-flex items-center gap-1 text-xs badge-success px-2 py-1 rounded"><CheckCircle className="w-3 h-3" />Kept</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs badge-error px-2 py-1 rounded"><XCircle className="w-3 h-3" />Rejected</span>
                          )}
                        </td>
                        <td className="text-xs text-muted-foreground max-w-[280px]">{c.reason}</td>
                        <td className="text-xs">
                          {c.email ? (
                            <span title={`Found on ${c.email_source}`} className="inline-flex items-center gap-1 font-mono">
                              <Mail className="w-3 h-3 text-green-500" />{c.email}
                            </span>
                          ) : c.contact_form_url ? (
                            <a href={c.contact_form_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
                              <FileText className="w-3 h-3" />Form
                            </a>
                          ) : c.status === "accepted" ? (
                            <span className="text-muted-foreground">None found</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
